import React from 'react';
import { useFormikContext } from 'formik';
import { useGetCustomAttributes } from '../../../hooks/queries/customAttributesQueries';
import { useRfiForm, useFormManageFields } from '../../RfiFormProvider';
import { ISSUE_ATTRIBUTE_NAMES } from '../../../constants/issue-attribute-names';
import { RfiCreateFormValues } from '../../types';
import { translate } from '../../../helpers/i18n';
import {
  TextAttributeField,
  NumericAttributeField,
  SelectAttributeField,
  MultiSelectAttributeField,
  DateAttributeField,
} from './components';
import { getValueForAttribute, updateAttribute } from '../../../components/HeadlessComponents';
import { getCustomAttributeError } from '../../helpers/buildManageFieldsValidation';

export const CustomAttributesFields = () => {
  const formik = useFormikContext<RfiCreateFormValues>();
  const { isSubmitting, refs } = useRfiForm();
  const { isCustomVisible, isCustomMandatory } = useFormManageFields();
  const { data: customAttributes, isLoading } = useGetCustomAttributes();

  if (isLoading || !customAttributes?.length) return null;

  const currentValues = formik.values[ISSUE_ATTRIBUTE_NAMES.CUSTOM_ATTRIBUTES];

  const customAttributesError = formik.errors?.[ISSUE_ATTRIBUTE_NAMES.CUSTOM_ATTRIBUTES];
  const customAttributesErrorBlob =
    typeof customAttributesError === 'string' ? customAttributesError : undefined;
  const hasSubmitAttempt = (formik.submitCount ?? 0) > 0;
  const fallbackMessage = translate('new.fieldRequired');

  const firstInvalidCustomAttributeId = hasSubmitAttempt
    ? customAttributes.find(
        (attr) =>
          isCustomVisible(attr.id) &&
          getCustomAttributeError(customAttributesErrorBlob, attr.id, fallbackMessage) !==
            undefined,
      )?.id
    : undefined;

  const handleChange = (attributeId: string, newValues: Array<string | null>) => {
    const updated = updateAttribute(currentValues, attributeId, newValues);
    formik.setFieldValue(ISSUE_ATTRIBUTE_NAMES.CUSTOM_ATTRIBUTES, updated);
  };

  const handleSingleChange = (attributeId: string) => (val: string) =>
    handleChange(attributeId, val ? [val] : []);

  const handleMultiChange = (attributeId: string) => (vals: string[]) =>
    handleChange(attributeId, vals);

  return (
    <React.Fragment>
      {customAttributes.map((attr) => {
        if (!isCustomVisible(attr.id)) return null;

        const values = getValueForAttribute(currentValues, attr.id);
        const required = isCustomMandatory(attr.id);
        const error = hasSubmitAttempt
          ? getCustomAttributeError(customAttributesErrorBlob, attr.id, fallbackMessage)
          : undefined;
        const fieldRef =
          attr.id === firstInvalidCustomAttributeId ? refs.customAttributes : undefined;

        switch (attr.dataType) {
          case 'text': {
            const commonProps = {
              attribute: attr,
              disabled: isSubmitting,
              required,
              error,
              fieldRef,
            };
            return (
              <TextAttributeField
                key={attr.id}
                {...commonProps}
                value={values[0] ?? ''}
                onChange={handleSingleChange(attr.id)}
                placeholder={translate('new.placeholder.customAttributeText')}
              />
            );
          }
          case 'numeric': {
            const commonProps = {
              attribute: attr,
              disabled: isSubmitting,
              required,
              error,
              fieldRef,
            };
            return (
              <NumericAttributeField
                key={attr.id}
                {...commonProps}
                value={values[0] ?? ''}
                onChange={handleSingleChange(attr.id)}
                placeholder={translate('new.placeholder.customAttributeNumeric')}
              />
            );
          }
          case 'select': {
            const commonProps = {
              attribute: attr,
              disabled: isSubmitting,
              required,
              error,
              fieldRef,
            };
            return (
              <SelectAttributeField
                key={attr.id}
                {...commonProps}
                value={values[0] ?? ''}
                onChange={handleSingleChange(attr.id)}
                placeholder={translate('new.placeholder.customAttributeSelect')}
              />
            );
          }
          case 'multi_select': {
            const commonProps = {
              attribute: attr,
              disabled: isSubmitting,
              required,
              error,
              fieldRef,
            };
            return (
              <MultiSelectAttributeField
                key={attr.id}
                {...commonProps}
                values={values}
                onChange={handleMultiChange(attr.id)}
                placeholder={translate('new.placeholder.customAttributeMultiSelect')}
              />
            );
          }
          case 'date': {
            const commonProps = {
              attribute: attr,
              disabled: isSubmitting,
              required,
              error,
              fieldRef,
            };
            return (
              <DateAttributeField
                key={attr.id}
                {...commonProps}
                value={values[0] ?? ''}
                onChange={handleSingleChange(attr.id)}
              />
            );
          }
          default:
            return null;
        }
      })}
    </React.Fragment>
  );
};
